// Client-side live-commentary controller: decide whether a scoring
// moment is worth a line (importance vs the chattiness slider), ask the
// server for the line + voice, and play it back one-at-a-time with an
// on-screen caption. The server does the Gemini + ElevenLabs work; this
// just gates, fetches, and plays.

import { api, getActiveOrgSlug } from "./api";

// Listen mode: POST a recorded audio segment (raw bytes) to the server,
// which transcribes it via Whisper. Not JSON, so it bypasses the `api`
// helper — we replicate its session-cookie + X-Org-Slug auth.
export async function transcribeAudio(
  blob: Blob,
): Promise<{ text: string; worth: boolean }> {
  const headers: Record<string, string> = {
    "Content-Type": blob.type || "audio/webm",
  };
  const slug = getActiveOrgSlug();
  if (slug) headers["X-Org-Slug"] = slug;
  const res = await fetch("/api/commentary/transcribe", {
    method: "POST",
    headers,
    body: blob,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`transcribe ${res.status}`);
  const j = (await res.json()) as { text?: string; worth?: boolean };
  return { text: (j.text ?? "").trim(), worth: j.worth === true };
}

// fun = encouraging/educational, funny = jokes, savage = dry roast.
export const COMMENTARY_MODES = ["fun", "funny", "savage"] as const;
export type CommentaryMode = (typeof COMMENTARY_MODES)[number];

// A persisted mode from before the mode rename could be "smack"/"offensive".
// Coerce to a current one so an old localStorage value doesn't leave the mode
// selector on nothing (and send a value the server would reject).
export function coerceMode(m: unknown): CommentaryMode {
  return m === "fun" || m === "funny" || m === "savage" ? m : "savage";
}

// The exact scoreboard state the client just displayed — sent so the
// announcer calls what the player saw, not a re-fetched (raceable) game.
export type CommentarySnapshot = {
  team1Score: number;
  team2Score: number;
  currentRound: number;
  targetScore: number;
  scoringTeam: 1 | 2 | null;
  netPoints: number;
  team1Gross: number | null;
  team2Gross: number | null;
  winningTeam: 1 | 2 | null;
  complete: boolean;
  throwsNext: 1 | 2 | null;
};

export type CommentaryEvent = {
  kind:
    | "round"
    | "game_complete"
    | "championship"
    | "intro"
    | "banter"
    // A round was undone — a fixed "scratch that" retraction, no LLM.
    | "retraction";
  netPoints: number;
  fourBagger: boolean;
  leadChange: boolean;
  gamePoint: boolean;
  // Listen-mode only: overheard table talk for the announcer to riff on.
  heard?: string;
  // Round + game_complete: authoritative scoreboard state.
  snapshot?: CommentarySnapshot;
};

export type CommentaryResult = {
  text: string;
  audioBase64: string;
  mime: string;
  // Intro only: coin-toss winner (team that throws first), for the flip
  // ceremony. null/undefined when no toss happened.
  coinToss?: 1 | 2 | null;
  // The model's addition only, without the deterministic score call — this is
  // what we keep as anti-repetition history, so old scores/stats don't get fed
  // back into the next prompt as if they were source data.
  flourish?: string;
  // The line generated but the VOICE failed (audioBase64 is ""). "quota" =
  // announcer voice out of credits. Caller shows the caption + a message
  // instead of silently playing nothing.
  voiceError?: "quota" | "failed";
};

// 0..1 importance of a moment. The biggest beats always clear even the
// lowest chattiness; a scored round clears the middle of the slider so the
// announcer actually calls the game as it's played. Only washes (no
// score) sit low enough to stay quiet unless chattiness is cranked up.
export function commentaryImportance(e: CommentaryEvent): number {
  if (e.kind === "intro") return 1;
  if (e.kind === "banter") return 1;
  if (e.kind === "championship") return 1;
  // A retraction corrects something already said out loud — never optional.
  if (e.kind === "retraction") return 1;
  if (e.kind === "game_complete") return 0.97;
  if (e.fourBagger) return 0.94;
  if (e.leadChange) return 0.88;
  if (e.gamePoint) return 0.85;
  if (e.netPoints >= 7) return 0.82;
  if (e.netPoints >= 4) return 0.78;
  if (e.netPoints >= 1) return 0.72; // any scoring round calls the play
  return 0.45; // washed round — nobody scored
}

// chattiness 0..100. At 0 only the biggest beats (~0.92+) speak; by the
// middle of the slider a scored round (0.58) clears; at 100 almost
// everything does. A little jitter keeps mid-settings feeling organic.
export function shouldSpeak(importance: number, chattiness: number): boolean {
  const c = Math.max(0, Math.min(100, chattiness)) / 100;
  const threshold = 0.92 - c * 0.88;
  return importance >= threshold - Math.random() * 0.08;
}

export async function requestCommentary(input: {
  gameId: string;
  mode: CommentaryMode;
  recentLines: string[];
  kind: CommentaryEvent["kind"];
  heard?: string;
  snapshot?: CommentarySnapshot;
}): Promise<CommentaryResult> {
  return api<CommentaryResult>("/commentary", {
    method: "POST",
    body: {
      gameId: input.gameId,
      mode: input.mode,
      recentLines: input.recentLines,
      heard: input.heard,
      snapshot: input.snapshot,
      event: { kind: input.kind },
    },
  });
}

// ---- Playback: one line at a time, freshest-wins ------------------
//
// Never overlap: while a line plays, keep at most ONE pending (the
// newest). When the current finishes, play the pending if it's still
// there. onCaption(text|null) drives the on-screen subtitle.

let current: HTMLAudioElement | null = null;
let currentUrl: string | null = null;
let pending: { url: string; text: string } | null = null;
let captionSink: ((t: string | null) => void) | null = null;

function revoke(url: string | null) {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

// Decode the base64 MP3 into a Blob object URL. iOS Safari frequently
// PLAYS a large `data:` audio URL but never fires its `ended` event —
// which would leave `current` stuck and silently queue every later line
// behind it (the "intro plays, then nothing" bug). Blob URLs fire the
// media events reliably.
function toBlobUrl(base64: string, mime: string): string {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

// Background-music ducking: the MusicController registers here so we can
// dip the music while a line plays and restore it after.
let duckHandler: ((ducked: boolean) => void) | null = null;
export function setMusicDuckHandler(fn: ((ducked: boolean) => void) | null) {
  duckHandler = fn;
}

// Listen mode: the Scorer registers here so we can pause the mic while a
// line plays — otherwise the announcer's own voice gets re-heard as
// "table talk" and it talks to itself.
let speakingHandler: ((speaking: boolean) => void) | null = null;
export function setSpeakingHandler(fn: ((speaking: boolean) => void) | null) {
  speakingHandler = fn;
}

function drain() {
  if (current || !pending) return;
  const next = pending;
  pending = null;
  const audio = new Audio(next.url);
  current = audio;
  currentUrl = next.url;
  captionSink?.(next.text);
  duckHandler?.(true);
  speakingHandler?.(true);
  // Safety net: if `ended`/`error` never fires (a stalled clip, a device
  // quirk), don't let `current` deadlock the queue so every later line is
  // silently stuck behind it. Force-advance a bit past the clip's real
  // length once we know it (so a longer intro is never clipped), with a
  // generous fallback if metadata never loads.
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  const arm = (ms: number) => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(done, ms);
  };
  const done = () => {
    // Guard against double-advance (ended + watchdog, or a stop in between).
    if (current !== audio) return;
    if (watchdog) {
      clearTimeout(watchdog);
      watchdog = null;
    }
    current = null;
    revoke(currentUrl);
    currentUrl = null;
    captionSink?.(null);
    duckHandler?.(false);
    speakingHandler?.(false);
    drain();
  };
  // Fallback until we learn the real duration. Kept tight (was 45s): if the
  // audio wedges without firing loadedmetadata/ended (a device/session quirk,
  // e.g. the Bluetooth/listen-mode audio-session churn), the queue is stuck
  // for this whole window and NOTHING new plays — which reads to the user as
  // "the announcer stopped and won't come back". 15s is longer than any real
  // clip takes to report metadata, and loadedmetadata immediately re-arms to
  // the exact duration below, so a normal clip is never cut short.
  arm(15_000);
  audio.onloadedmetadata = () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      arm(audio.duration * 1000 + 4000);
    }
  };
  audio.onended = done;
  audio.onerror = done;
  setAudioSession("playback");
  audio.play().catch(done);
}

// iOS/Safari only routes media to the media output (incl. Bluetooth A2DP)
// when the audio SESSION is "playback". Listen mode's getUserMedia flips it
// to "play-and-record" (needed for the mic), which can strand ALL app audio
// on the phone earpiece — even after the mic is released, and even for the
// announcer + music. Setting it back to "playback" for playback restores
// Bluetooth output. Feature-detected (Safari 16.4+); a no-op elsewhere.
export function setAudioSession(type: "playback" | "play-and-record"): void {
  try {
    const s = (
      navigator as unknown as { audioSession?: { type: string } }
    ).audioSession;
    if (s) s.type = type;
  } catch {
    /* unsupported — ignore */
  }
}

// A short two-tone chime played through the SAME HTMLAudio path the
// announcer uses, so the user can confirm where the app's audio is routing
// (Bluetooth speaker vs the phone). Forces the playback session first.
export function playTestSound(): void {
  setAudioSession("playback");
  const url = chimeWavUrl();
  const a = new Audio(url);
  const cleanup = () => revoke(url);
  a.onended = cleanup;
  a.onerror = cleanup;
  a.play().catch(cleanup);
}

function chimeWavUrl(): string {
  const sr = 44100;
  const ms = 700;
  const n = Math.floor((sr * ms) / 1000);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const ws = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  ws(0, "RIFF");
  v.setUint32(4, 36 + n * 2, true);
  ws(8, "WAVE");
  ws(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sr, true);
  v.setUint32(28, sr * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  ws(36, "data");
  v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    // Rise then fall through two notes, with a short fade to avoid clicks.
    const freq = t < ms / 2000 ? 587 : 880; // D5 → A5
    const env = Math.min(1, i / 1500, (n - i) / 1500);
    const s = Math.sin(2 * Math.PI * freq * t) * 0.35 * env;
    v.setInt16(44 + i * 2, s * 32767, true);
  }
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}

export function speakCommentary(
  result: CommentaryResult,
  onCaption: (t: string | null) => void,
): void {
  captionSink = onCaption;
  // Voice failed but the line generated — show the caption so the announcer's
  // words are still visible, then clear it. No audio to queue.
  if (!result.audioBase64) {
    if (pending) {
      revoke(pending.url);
      pending = null;
    }
    onCaption(result.text);
    window.setTimeout(() => {
      // Only clear if nothing newer took over the caption.
      if (!current && !pending) onCaption(null);
    }, 6000);
    return;
  }
  // Freshest-wins: drop any stale queued clip (and free its blob).
  if (pending) revoke(pending.url);
  pending = {
    url: toBlobUrl(result.audioBase64, result.mime),
    text: result.text,
  };
  drain();
}

export function stopCommentary(): void {
  if (pending) revoke(pending.url);
  pending = null;
  if (current) {
    try {
      current.pause();
    } catch {
      /* ignore */
    }
    current = null;
  }
  revoke(currentUrl);
  currentUrl = null;
  captionSink?.(null);
  // Restore anything we suspended for playback so music swells back and
  // the mic un-pauses even if we were cut off mid-line.
  duckHandler?.(false);
  speakingHandler?.(false);
}
